import { SITE_NAME } from "@/constants/seo";

// Power words for CTR optimization
const POWER_WORDS = {
  trust: ["Verificado", "Certificado", "Oficial", "Profesional", "Autorizado"],
  urgency: ["Ahora", "Hoy", "Actualizado"],
  value: ["Mejor", "Top", "Premium", "Exclusivo", "Recomendado"],
  action: ["Descubre", "Encuentra", "Compara", "Reserva"],
  social: ["Opiniones Reales", "Valorado", "Popular", "Favorito"],
};

// Hash function for consistent rotation
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

// Truncate to max length with ellipsis
const truncateTitle = (title: string, maxLength: number = 60): string => {
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength - 3).trim() + "...";
};

// ============================================
// LISTING TITLES
// ============================================

interface ListingTitleParams {
  name: string;
  city: string;
  type: string;
  rating?: number;
  isPremium?: boolean;
  tags?: string[];
  /** Listing slug — used to disambiguate duplicate name+city+type rows so titles stay unique */
  slug?: string;
}

/**
 * If the slug ends in -N (N>=2), returns " - Sede N" so duplicates get unique titles.
 * Returns empty string for the canonical (first) slug or when slug is missing.
 */
const slugDisambiguator = (slug?: string): string => {
  if (!slug) return "";
  const m = slug.match(/-(\d+)$/);
  if (!m) return "";
  const n = parseInt(m[1], 10);
  if (!n || n < 2) return "";
  return ` - Sede ${n}`;
};

const LISTING_TITLE_PATTERNS = [
  (p: ListingTitleParams) => `${p.name} ${p.city} - ${p.type} Verificado`,
  (p: ListingTitleParams) => `${p.name} en ${p.city} | ${p.type} Recomendado`,
  (p: ListingTitleParams) => `${p.name} - ${p.type} Premium en ${p.city}`,
  (p: ListingTitleParams) => `${p.name} ${p.city} | Tu ${p.type} de Confianza`,
  (p: ListingTitleParams) => `${p.name} - Mejor ${p.type} en ${p.city}`,
  (p: ListingTitleParams) => `${p.name} ${p.city} | ${p.type} Certificado`,
  (p: ListingTitleParams) => `${p.name} en ${p.city} - ${p.type} Destacado`,
  (p: ListingTitleParams) => `${p.name} | ${p.type} Profesional ${p.city}`,
];

const PREMIUM_LISTING_PATTERNS = [
  (p: ListingTitleParams) => `${p.name} ${p.city} - ${p.type} Premium`,
  (p: ListingTitleParams) => `${p.name} ${p.city} | ${p.type} Destacado`,
  (p: ListingTitleParams) => `${p.name} - ${p.type} Exclusivo en ${p.city}`,
];

export const generateListingTitle = (params: ListingTitleParams): string => {
  const patterns = params.isPremium ? PREMIUM_LISTING_PATTERNS : LISTING_TITLE_PATTERNS;
  const hash = hashString(`${params.name}-${params.city}-${params.type}-${params.slug || ''}`);
  const patternIndex = hash % patterns.length;
  const title = patterns[patternIndex](params) + slugDisambiguator(params.slug);
  return truncateTitle(title);
};

// ============================================
// ARTICLE TITLES
// ============================================

interface ArticleTitleParams {
  title: string;
  category: string;
  readingTime?: number;
}

const ARTICLE_TITLE_PATTERNS = [
  (p: ArticleTitleParams) => `${p.title} | Guía Completa`,
  (p: ArticleTitleParams) => `${p.title} - ${p.category} Profesional`,
  (p: ArticleTitleParams) => `${p.title} | Consejos de Expertos`,
  (p: ArticleTitleParams) => `${p.title} - Guía Actualizada`,
  (p: ArticleTitleParams) => `${p.title} | ${p.category} Verificado`,
  (p: ArticleTitleParams) => `${p.title} - Todo lo que Necesitas Saber`,
  (p: ArticleTitleParams) => `${p.title} | Información Experta`,
  (p: ArticleTitleParams) => `${p.title} - Guía Práctica ${p.category}`,
];

export const generateArticleTitle = (params: ArticleTitleParams): string => {
  const hash = hashString(params.title);
  const patternIndex = hash % ARTICLE_TITLE_PATTERNS.length;
  const title = ARTICLE_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// TRAINER TITLES
// ============================================

interface TrainerTitleParams {
  name: string;
  city: string;
  specialty?: string;
  yearsExperience?: number;
}

const TRAINER_TITLE_PATTERNS = [
  (p: TrainerTitleParams) => `${p.name} - Entrenador Personal ${p.city}`,
  (p: TrainerTitleParams) => `${p.name} ${p.city} | Entrenador Certificado`,
  (p: TrainerTitleParams) => `${p.name} - Personal Trainer en ${p.city}`,
  (p: TrainerTitleParams) => `${p.name} ${p.city} | Experto Fitness`,
  (p: TrainerTitleParams) => `${p.name} - Tu Entrenador en ${p.city}`,
];

const SPECIALTY_TRAINER_PATTERNS = [
  (p: TrainerTitleParams) => `${p.name} - ${p.specialty} en ${p.city}`,
  (p: TrainerTitleParams) => `${p.name} ${p.city} | Especialista ${p.specialty}`,
  (p: TrainerTitleParams) => `${p.name} - Experto ${p.specialty} ${p.city}`,
];

export const generateTrainerTitle = (params: TrainerTitleParams): string => {
  const patterns = params.specialty ? SPECIALTY_TRAINER_PATTERNS : TRAINER_TITLE_PATTERNS;
  const hash = hashString(`${params.name}-${params.city}`);
  const patternIndex = hash % patterns.length;
  const title = patterns[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// EVENT TITLES
// ============================================

interface EventTitleParams {
  title: string;
  city?: string;
  date?: string;
  eventType: string;
  price?: number;
}

const EVENT_TITLE_PATTERNS = [
  (p: EventTitleParams) => `${p.title} ${p.city || ""} | ${p.eventType}`.trim(),
  (p: EventTitleParams) => `${p.title} | ${p.eventType} en ${p.city || "España"}`,
  (p: EventTitleParams) => `${p.title} - ${p.eventType} Deportivo`,
  (p: EventTitleParams) => `${p.title} en ${p.city || "España"} | Evento ${p.eventType}`,
];

export const generateEventTitle = (params: EventTitleParams): string => {
  const hash = hashString(params.title);
  const patternIndex = hash % EVENT_TITLE_PATTERNS.length;
  const title = EVENT_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// VIDEO TITLES
// ============================================

interface VideoTitleParams {
  title: string;
  category?: string;
  tags?: string[];
}

const VIDEO_TITLE_PATTERNS = [
  (p: VideoTitleParams) => `${p.title} | Tutorial ${p.category || "Fitness"}`,
  (p: VideoTitleParams) => `${p.title} - Video ${p.category || "Deportivo"}`,
  (p: VideoTitleParams) => `${p.title} | Guía Visual Completa`,
  (p: VideoTitleParams) => `${p.title} - Aprende ${p.category || "Fitness"}`,
];

export const generateVideoTitle = (params: VideoTitleParams): string => {
  const hash = hashString(params.title);
  const patternIndex = hash % VIDEO_TITLE_PATTERNS.length;
  const title = VIDEO_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// NUTRITIONIST TITLES
// ============================================

interface NutritionistTitleParams {
  name: string;
  city: string;
  specialty?: string;
}

const NUTRITIONIST_TITLE_PATTERNS = [
  (p: NutritionistTitleParams) => `${p.name} - Nutricionista ${p.city}`,
  (p: NutritionistTitleParams) => `${p.name} ${p.city} | Nutricionista Certificado`,
  (p: NutritionistTitleParams) => `${p.name} - Dietista Profesional ${p.city}`,
  (p: NutritionistTitleParams) => `${p.name} ${p.city} | Nutrición Deportiva`,
  (p: NutritionistTitleParams) => `${p.name} - Experto Nutrición en ${p.city}`,
];

export const generateNutritionistTitle = (params: NutritionistTitleParams): string => {
  const hash = hashString(`${params.name}-${params.city}`);
  const patternIndex = hash % NUTRITIONIST_TITLE_PATTERNS.length;
  const title = NUTRITIONIST_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// PHYSIOTHERAPIST TITLES
// ============================================

interface PhysiotherapistTitleParams {
  name: string;
  city: string;
  specialty?: string;
}

const PHYSIO_TITLE_PATTERNS = [
  (p: PhysiotherapistTitleParams) => `${p.name} - Fisioterapeuta ${p.city}`,
  (p: PhysiotherapistTitleParams) => `${p.name} ${p.city} | Fisioterapeuta Deportivo`,
  (p: PhysiotherapistTitleParams) => `${p.name} - Fisio Profesional ${p.city}`,
  (p: PhysiotherapistTitleParams) => `${p.name} ${p.city} | Rehabilitación Deportiva`,
  (p: PhysiotherapistTitleParams) => `${p.name} - Experto Fisioterapia ${p.city}`,
];

export const generatePhysiotherapistTitle = (params: PhysiotherapistTitleParams): string => {
  const hash = hashString(`${params.name}-${params.city}`);
  const patternIndex = hash % PHYSIO_TITLE_PATTERNS.length;
  const title = PHYSIO_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// DIRECTORY TITLES
// ============================================

interface DirectoryTitleParams {
  type?: string;
  city?: string;
  activity?: string;
  count?: number;
}

export const generateDirectoryTitle = (params: DirectoryTitleParams): string => {
  const { type, city, activity } = params;
  
  if (activity && city) {
    return truncateTitle(`${activity} en ${city} - Los Mejores Centros`);
  }
  
  if (city) {
    return truncateTitle(`Gimnasios y Centros Fitness ${city} | Directorio`);
  }
  
  if (type) {
    return truncateTitle(`Mejores ${type} España - Directorio Completo`);
  }
  
  return truncateTitle(`Directorio Fitness España - Gimnasios y Centros`);
};

// ============================================
// LANDING PAGE TITLES (Unique per page)
// ============================================

export const LANDING_TITLES: Record<string, string> = {
  fitness: `Marketing para Gimnasios España | Más Clientes`,
  crossfit: `Promoción Boxes CrossFit | Atrae Atletas`,
  yoga: `Marketing Estudios Yoga | Más Alumnos`,
  pilates: `Promoción Estudios Pilates | Crece Tu Centro`,
  bienestar: `Marketing Centros Bienestar | Más Visibilidad`,
  losMejores: `Top Profesionales Deportivos España | Rankings`,
  entrenadores: `Entrenadores Personales España | Directorio`,
  nutricionistas: `Nutricionistas Deportivos España | Expertos`,
  fisioterapeutas: `Fisioterapeutas Deportivos España | Top`,
  eventos: `Eventos Deportivos España | Calendario`,
  videos: `Videos Fitness y Tutoriales | Aprende`,
  articulos: `Blog Fitness y Nutrición | Consejos Expertos`,
  tiendas: `Tiendas Deportivas España | Directorio`,
  'artes-marciales': `Academias Artes Marciales | Más Alumnos`,
  baile: `Academias de Baile España | Más Alumnos`,
  'centros-bienestar': `Centros Bienestar España | Sin Comisiones`,
};

// Generate landing page title from type
export const generateLandingPageTitle = (type: string): string => {
  return LANDING_TITLES[type] || `${type.charAt(0).toUpperCase() + type.slice(1)} España | Ekselencia`;
};

// ============================================
// SPORT PROFESSIONAL TITLES
// ============================================

interface SportProfessionalTitleParams {
  name: string;
  specialty: string;
  city?: string;
  country?: string;
  /** Unique slug — used to disambiguate duplicate name+specialty rows so titles stay unique */
  slug?: string;
}

const SPORT_PRO_TITLE_PATTERNS = [
  (p: SportProfessionalTitleParams) => `${p.name} - ${p.specialty} ${p.city || p.country || ""}`.trim(),
  (p: SportProfessionalTitleParams) => `${p.name} | ${p.specialty} Profesional`,
  (p: SportProfessionalTitleParams) => `${p.name} - ${p.specialty} en ${p.city || p.country || "España"}`,
  (p: SportProfessionalTitleParams) => `${p.name} ${p.city || ""} | ${p.specialty} TOP`.trim(),
];

/**
 * Builds a deterministic, human-readable suffix derived from the part of the slug that
 * differs from the name. Guarantees that two rows with the same name but different slugs
 * produce different titles. Returns "" when slug equals the canonical name slug.
 */
const slugTailQualifier = (slug?: string, primaryName?: string): string => {
  if (!slug) return "";
  const toSlug = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const nameSlug = primaryName ? toSlug(primaryName) : "";
  let tail = nameSlug && slug.startsWith(nameSlug)
    ? slug.slice(nameSlug.length).replace(/^-/, "")
    : "";
  if (!tail) {
    const m = slug.match(/-(\d+)$/);
    return m && parseInt(m[1], 10) >= 2 ? ` · #${m[1]}` : "";
  }
  if (/^\d+$/.test(tail)) return ` · #${tail}`;
  const human = tail.split("-").filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  return ` · ${human}`;
};

export const generateSportProfessionalTitle = (params: SportProfessionalTitleParams): string => {
  const hash = hashString(`${params.name}-${params.specialty}-${params.slug || ''}`);
  const patternIndex = hash % SPORT_PRO_TITLE_PATTERNS.length;
  const base = SPORT_PRO_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(base + slugTailQualifier(params.slug, params.name));
};
